import { RefObject, useEffect, useMemo, useState } from 'react';
import { throttle } from 'throttle-debounce';

import { useViewportDimensions } from '../../../hooks/useViewportDimensions';
import { useViewportScroll } from '../../../hooks/useViewportScroll';

/** Matches the sticky header's own scroll handler. */
const THROTTLE_MS = 100;

/**
 * Whether every edge of `ref` is inside the viewport.
 *
 * A block taller than the viewport can never fit inside it, so filling the
 * viewport top to bottom counts too — without that the sticky CTA would never
 * get out of its way on a short screen.
 *
 * Measured from the element's own rect rather than an `IntersectionObserver`
 * ratio. The ratio at which a block fills the viewport is the viewport's share
 * of the block's height — 800/1200 ≈ 0.67, 800/1000 = 0.8 — so no fixed list
 * of thresholds fires at that moment, and the observer stayed silent through
 * exactly the case it was there to catch.
 */
export const useFullyInView = <T extends Element>(
  ref: RefObject<T | null>,
): boolean => {
  const { scrollY } = useViewportScroll();
  const { height: viewportHeight } = useViewportDimensions();
  const [fullyInView, setFullyInView] = useState(false);

  const measure = useMemo(
    () =>
      throttle(THROTTLE_MS, false, () => {
        const node = ref.current;

        // The provider measures the viewport on mount, so the height is only
        // ever 0 on the first render — when the bar should be showing anyway.
        if (!node || viewportHeight === 0) {
          setFullyInView(false);

          return;
        }

        const { bottom, top } = node.getBoundingClientRect();

        setFullyInView(
          // Inside the viewport, or covering it top to bottom.
          (top >= 0 && bottom <= viewportHeight) ||
            (top <= 0 && bottom >= viewportHeight),
        );
      }),
    [ref, viewportHeight],
  );

  useEffect(() => {
    measure();
  }, [measure, scrollY]);

  useEffect(() => () => measure.cancel(), [measure]);

  return fullyInView;
};
